import { agents } from "../data/agents";
import { apps } from "../data/apps";
import { components } from "../data/components";
import { APP_ANGLES, useActiveNode } from "../hooks/useActiveNode";
import { STAGE, useStageZoom } from "../hooks/useStageZoom";
import { AgentNode } from "./AgentNode";
import { AppNode } from "./AppNode";
import { CenterHub } from "./CenterHub";
import { ComponentNode } from "./ComponentNode";
import { InfoPanel } from "./InfoPanel";
import { ProofNode } from "./ProofNode";
import { getConnectedNames, RelationshipLines } from "./RelationshipLines";
import { RingStructure } from "./RingStructure";
import { StatusLegend } from "./StatusLegend";

const ORANGE = "#F28132";
const DARK_BG = "#0a0b0e";
const PANEL_BG = "#14161c";

export function PlatformMap() {
  const {
    active,
    pinned,
    hoverAgent,
    hoverApp,
    hoverComponent,
    clearHover,
    togglePinAgent,
    togglePinApp,
    togglePinComponent,
    clearPin
  } = useActiveNode();

  const mainRingApps = apps.filter(
    (a) => a.category === "customer" || a.category === "internal"
  );
  const b2procApps = apps.filter((a) => a.category === "b2proc");
  const connected = getConnectedNames(active);
  const zoom = useStageZoom();

  return (
    <main
      className="flex min-h-screen w-full items-center justify-center p-4 md:p-8 xl:h-screen xl:overflow-hidden xl:p-0"
      style={{ backgroundColor: DARK_BG }}
    >
      {/* On xl the whole map is a fixed canvas zoomed to fit the viewport
          (see useStageZoom), so hovering never resizes or reflows anything. */}
      <div
        className="w-full max-w-7xl xl:flex xl:max-w-none xl:flex-col xl:p-6"
        style={
          zoom === null
            ? undefined
            : { width: STAGE.width, height: STAGE.height, zoom }
        }
      >
        <header className="mb-8 flex items-end justify-between xl:mb-3">
          <div>
            <div
              className="mb-2 text-[10px] uppercase tracking-[0.25em]"
              style={{ color: ORANGE }}
            >
              OpenSesame
            </div>
            <h1 className="text-2xl font-semibold leading-tight text-white md:text-3xl">
              Platform Map
            </h1>
          </div>
          <div className="hidden text-right text-[11px] leading-snug text-white/40 md:block">
            <div>Eight agents.</div>
            <div>Eight applications.</div>
            <div>Four shared services.</div>
            <div className="mt-1" style={{ color: ORANGE, opacity: 0.75 }}>
              Plus the worker layer.
            </div>
          </div>
        </header>

        <div className="grid gap-6 xl:min-h-0 xl:flex-1 xl:grid-cols-[640px_minmax(0,1fr)] xl:gap-7">
          <section
            className="flex flex-col items-center justify-center"
            onMouseLeave={clearHover}
            aria-label="Platform diagram"
          >
            <div className="relative aspect-square w-full max-w-[720px] xl:w-[628px]">
              <RingStructure agentCount={agents.length} />

              <RelationshipLines active={active} />

              <CenterHub />

              {components.map((component) => {
                const angle = APP_ANGLES[component.name];
                if (angle === undefined) return null;
                const isActive =
                  active?.kind === "component" &&
                  active.data.name === component.name;
                const isPinned =
                  pinned?.kind === "component" &&
                  pinned.data.name === component.name;
                return (
                  <ComponentNode
                    key={component.name}
                    component={component}
                    angle={angle}
                    isActive={isActive}
                    isPinned={isPinned}
                    isConnected={!isActive && connected.has(component.name)}
                    onHover={hoverComponent}
                    onClick={togglePinComponent}
                  />
                );
              })}

              {agents.map((agent, i) => {
                const isActive =
                  active?.kind === "agent" && active.data.name === agent.name;
                const isPinned =
                  pinned?.kind === "agent" && pinned.data.name === agent.name;
                return (
                  <AgentNode
                    key={agent.name}
                    agent={agent}
                    index={i}
                    total={agents.length}
                    isActive={isActive}
                    isPinned={isPinned}
                    isConnected={!isActive && connected.has(agent.name)}
                    onHover={hoverAgent}
                    onClick={togglePinAgent}
                  />
                );
              })}

              {mainRingApps.map((app) => {
                const angle = APP_ANGLES[app.name];
                if (angle === undefined) return null;
                const isActive =
                  active?.kind === "app" && active.data.name === app.name;
                const isPinned =
                  pinned?.kind === "app" && pinned.data.name === app.name;
                return (
                  <AppNode
                    key={app.name}
                    app={app}
                    angle={angle}
                    isActive={isActive}
                    isPinned={isPinned}
                    isConnected={!isActive && connected.has(app.name)}
                    onHover={hoverApp}
                    onClick={togglePinApp}
                  />
                );
              })}

              {b2procApps.map((app) => {
                const isActive =
                  active?.kind === "app" && active.data.name === app.name;
                const isPinned =
                  pinned?.kind === "app" && pinned.data.name === app.name;
                return (
                  <ProofNode
                    key={app.name}
                    app={app}
                    isActive={isActive}
                    isPinned={isPinned}
                    isConnected={!isActive && connected.has(app.name)}
                    onHover={hoverApp}
                    onClick={togglePinApp}
                  />
                );
              })}
            </div>
            <StatusLegend />
          </section>

          <aside className="xl:min-h-0" aria-label="Details panel">
            <div
              className="relative h-full min-h-[560px] rounded-2xl p-7 transition-all duration-300 md:p-8 xl:min-h-0 xl:overflow-hidden xl:p-7"
              style={{
                backgroundColor: PANEL_BG,
                border: "1px solid rgba(255,255,255,0.15)"
              }}
            >
              {pinned && (
                <button
                  type="button"
                  onClick={clearPin}
                  aria-label="Clear selection"
                  className="absolute right-4 top-4 rounded-full p-2 text-white/40 transition-colors hover:bg-white/5 hover:text-white/80"
                  style={{ fontSize: 14, lineHeight: 1 }}
                >
                  ×
                </button>
              )}
              <InfoPanel active={active} />
            </div>
          </aside>
        </div>

        <footer className="mt-6 flex items-center justify-between text-[10px] uppercase tracking-wider text-white/30 xl:mt-3">
          <div>Internal platform map</div>
          <div>v. 2026.09</div>
        </footer>
      </div>
    </main>
  );
}
